import { useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

/**
 * Custom hook for downloading workspace repositories as ZIP files
 */
const useRepositoryDownload = () => {
  const authenticatedApi = useAuthenticatedApi();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  const downloadRepository = async (workspaceId, path = null) => {
    setIsDownloading(true);
    setError(null);

    try {
      const token = await authenticatedApi.getToken();
      const params = new URLSearchParams();
      if (path) params.append('path', path);

      const url = `${authenticatedApi.getBaseUrl()}${apiEndpoints.workspaceDownloadRepository(workspaceId)}?${params}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(errorData.detail || 'Failed to download repository');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : 'repository.zip';

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

    } catch (err) {
      console.error('Repository download failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadRepository,
    isDownloading,
    error,
    clearError: () => setError(null),
  };
};

export default useRepositoryDownload; 